import React from "react";

import { ColorPickerField, Input, Title } from "@amphore-dev/amphore-lib";
import { Formik, Form, FormikProps } from "formik";
import { t } from "i18next";

import { fieldError } from "@/utils/UForms";

import { TViewEditFormValues } from "@/types";

import {
	ViewEditFormInitialValues,
	ViewEditFormValidation,
} from "./ViewEditFormValidation";
import { ViewSelectionFields } from "./ViewSelectionFields";

interface IViewEditFormProps {
	onSubmit: (
		values: TViewEditFormValues,
		formikHelpers: unknown
	) => Promise<unknown>;
	initialValues?: TViewEditFormValues;
	innerRef?: React.Ref<FormikProps<TViewEditFormValues>>;
}

/**
 * What a view is: a name, a colour, and what it gathers — projects one
 * by one, clients whole (their projects follow, present and future).
 * Nothing about rhythm or rates: those belong to the person and to the
 * client (docs/modele.md, phase 6).
 */
export const ViewEditForm: React.FC<IViewEditFormProps> = ({
	onSubmit,
	innerRef,
	...props
}) => {
	const handleSubmit = (
		values: TViewEditFormValues,
		formikHelpers: unknown
	) => {
		return onSubmit(values, formikHelpers);
	};

	return (
		<Formik
			initialValues={{
				...ViewEditFormInitialValues,
				...props.initialValues,
			}}
			onSubmit={handleSubmit}
			validationSchema={ViewEditFormValidation()}
			innerRef={innerRef}
		>
			{(formik) => {
				const { values, setFieldValue } = formik;
				return (
					<Form className="col h-full [&>div]:mb-6 [&>div]:flex [&>div]:flex-col [&>div]:gap-4 [&>div]:pb-8 [&>div:not(:last-child)]:border-b">
						<div>
							<Title size="h4">{t("forms.view.identity")}</Title>
							<div className="row z-10 w-full items-center gap-4">
								<div className="w-full">
									<Input
										value={values.name}
										onChange={(value) =>
											setFieldValue("name", value)
										}
										placeholder={t("views.namePlaceholder")}
										error={fieldError(formik, "name")}
										className="w-full"
									/>
								</div>
								<ColorPickerField
									value={values.color || "#000000"}
									onChange={(color: string) =>
										setFieldValue("color", color)
									}
									className="bg-white"
								/>
							</div>
						</div>
						<div>
							<Title size="h4">{t("forms.view.selection")}</Title>
							<ViewSelectionFields
								projects={values.projects ?? []}
								clients={values.clients ?? []}
								onChange={setFieldValue}
							/>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};
